// Seller draft page → send to Etsy: confirm dialog + result screen (plan
// docs/plans/2026-10-26-etsy-listing-push-v2-plan.md P4; mockup approved by
// Volkan 2026-10-27). States: sending (live steps) · ready · partly done
// ("Retry the missing steps" → same Etsy draft) · uncertain (Etsy didn't
// answer — link the draft found on Etsy, or confirm there is none) · refused.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiUrl } from '../../../lib/api'
import { toast } from '../../../lib/toast'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { useT } from '../../../i18n'
import { etsyPublishI18n, type EtsyPublishStrings } from '../../../i18n/seller/etsy-publish'
import type { EtsyDraft } from './etsy-draft.lib'
import {
  ETSY_DRAFTS_URL,
  isRunning,
  publishKey,
  STEP_ORDER,
  type PublishStep,
  type PublishView,
} from './etsy-publish.lib'
import { formatCurrency } from '../../../lib/currency'

async function post(path: string, body?: unknown): Promise<PublishView> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { detail?: { message?: string } }
    throw new Error(data.detail?.message ?? `${res.status}`)
  }
  return (await res.json()) as PublishView
}

// --- confirm dialog ---------------------------------------------------------

export function SendConfirmDialog({
  open,
  onClose,
  listingId,
  draft,
  shopName,
  beforeSend,
}: {
  open: boolean
  onClose: () => void
  listingId: string
  draft: EtsyDraft
  shopName: string
  /** Saves unsaved edits first; resolves false when the save failed. */
  beforeSend: () => Promise<boolean>
}) {
  const t = useT(etsyPublishI18n).confirm
  const qc = useQueryClient()
  const enabled = draft.combos.filter((c) => c.enabled)
  const prices = enabled.map((c) => Number(c.price)).filter((p) => Number.isFinite(p) && p > 0)
  const range = prices.length
    ? `${formatCurrency(Math.min(...prices))} – ${formatCurrency(Math.max(...prices))}`
    : '—'
  const send = useMutation({
    mutationFn: async () => {
      if (!(await beforeSend())) throw new Error('save')
      return post(`/listings/${listingId}/etsy-publish`)
    },
    onSuccess: (view) => {
      qc.setQueryData(publishKey(listingId), view)
      onClose()
    },
    onError: (err: Error) => {
      if (err.message !== 'save') toast.error(t.failed, { description: err.message, audience: 'seller' })
    },
  })
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {open ? (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.title}</DialogTitle>
            <DialogDescription>{t.note}</DialogDescription>
          </DialogHeader>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-[var(--color-text-muted)]">{t.store}</dt>
            <dd className="m-0 text-[var(--color-text)]">{shopName}</dd>
            <dt className="text-[var(--color-text-muted)]">{t.options}</dt>
            <dd className="m-0 text-[var(--color-text)]">{t.optionsValue(draft.combos.length, enabled.length)}</dd>
            <dt className="text-[var(--color-text-muted)]">{t.priceRange}</dt>
            <dd className="m-0 tabular-nums text-[var(--color-text)]">{range}</dd>
            <dt className="text-[var(--color-text-muted)]">{t.photos}</dt>
            <dd className="m-0 text-[var(--color-text)]">{draft.photos.length}</dd>
            <dt className="text-[var(--color-text-muted)]">{t.state}</dt>
            <dd className="m-0 text-[var(--color-text)]">{t.stateValue}</dd>
          </dl>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={send.isPending}>
              {t.cancel}
            </Button>
            <Button onClick={() => send.mutate()} disabled={send.isPending}>
              {send.isPending ? t.sending : t.send}
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}

// --- result screen ------------------------------------------------------------

type Tone = 'ok' | 'warn' | 'bad' | 'run'

const toneBox: Record<Tone, string> = {
  ok: 'border-[color-mix(in_srgb,var(--color-success)_30%,transparent)] bg-[color-mix(in_srgb,var(--color-success)_8%,var(--color-surface))]',
  warn: 'border-[color-mix(in_srgb,var(--color-warning)_32%,transparent)] bg-[color-mix(in_srgb,var(--color-warning)_9%,var(--color-surface))]',
  bad: 'border-[color-mix(in_srgb,var(--color-danger)_30%,transparent)] bg-[color-mix(in_srgb,var(--color-danger)_8%,var(--color-surface))]',
  run: 'border-[var(--color-border)] bg-[var(--color-surface-muted)]',
}
const toneIcon: Record<Tone, string> = {
  ok: 'bg-[var(--color-success)] text-[var(--color-surface)]',
  warn: 'bg-[var(--color-warning)] text-[var(--color-surface)]',
  bad: 'bg-[var(--color-danger)] text-[var(--color-surface)]',
  run: 'bg-[var(--color-border-strong)] text-[var(--color-text)]',
}

function Banner({ tone, icon, title, body }: { tone: Tone; icon: string; title: string; body: string }) {
  return (
    <div className={`flex items-start gap-3 rounded-[var(--card-radius)] border px-4 py-3 ${toneBox[tone]}`} role="status">
      <span aria-hidden="true" className={`grid h-7 w-7 flex-none place-items-center rounded-full font-bold ${toneIcon[tone]}`}>
        {icon}
      </span>
      <div>
        <h2 className="m-0 text-base font-semibold text-[var(--color-text)]">{title}</h2>
        <p className="m-0 mt-0.5 text-[13px] text-[var(--color-text-muted)]">{body}</p>
      </div>
    </div>
  )
}

function stepDetail(t: EtsyPublishStrings, id: string, step: PublishStep | undefined): string | null {
  if (!step) return null
  if (step.status === 'skipped') return t.stepDetail.skipped
  if (step.status === 'failed' || step.status === 'uncertain') return step.message ?? null
  switch (id) {
    case 'listing':
      return t.stepDetail.listing(step.tags ?? 0, step.materials ?? 0)
    case 'photos':
      return step.failed?.length
        ? step.failed.map((f) => t.stepDetail.photoFailed(f.rank, f.label, f.message)).join(' ')
        : t.stepDetail.photosOk
    case 'options':
      return t.stepDetail.options(step.total ?? 0, step.enabled ?? 0, step.hidden ?? 0)
    case 'variation_photos':
      return step.missing?.length ? t.stepDetail.variationMissing(step.missing) : null
    case 'personalization':
      return t.stepDetail.personalization(step.count ?? 0)
    case 'binding':
      return t.stepDetail.binding
    default:
      return null
  }
}

export function StepList({ t, view, running }: { t: EtsyPublishStrings; view: PublishView; running: boolean }) {
  const firstPending = STEP_ORDER.find((id) => !view.steps[id])
  return (
    <ol className="m-0 grid list-none p-0">
      {STEP_ORDER.map((id) => {
        const step = view.steps[id]
        const state = step
          ? step.status === 'ok' || step.status === 'skipped'
            ? 'ok'
            : step.status === 'warn'
              ? 'warn'
              : 'bad'
          : running && id === firstPending
            ? 'run'
            : 'wait'
        const dot = {
          ok: 'bg-[color-mix(in_srgb,var(--color-success)_18%,transparent)] text-[var(--color-success)]',
          warn: 'bg-[color-mix(in_srgb,var(--color-warning)_20%,transparent)] text-[var(--color-warning)]',
          bad: 'bg-[color-mix(in_srgb,var(--color-danger)_16%,transparent)] text-[var(--color-danger)]',
          run: 'bg-[var(--color-surface-muted)] text-[var(--color-text)] motion-safe:animate-pulse',
          wait: 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]',
        }[state]
        const detail = stepDetail(t, id, step)
        const count =
          step && step.total !== undefined && step.status !== 'skipped' ? `${step.count ?? 0}/${step.total}` : step ? '' : '—'
        return (
          <li key={id} className="grid grid-cols-[26px_minmax(0,1fr)_auto] items-start gap-2.5 border-b border-[var(--color-border)] py-2.5 last:border-b-0">
            <span aria-hidden="true" className={`grid h-[22px] w-[22px] place-items-center rounded-full text-xs font-bold ${dot}`}>
              {state === 'ok' ? '✓' : state === 'warn' || state === 'bad' ? '!' : state === 'run' ? '•' : '·'}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--color-text)]">{t.steps[id]}</div>
              {detail ? <div className="text-[12.5px] text-[var(--color-text-muted)]">{detail}</div> : null}
            </div>
            <span className="whitespace-nowrap text-[12.5px] tabular-nums text-[var(--color-text-muted)]">{count}</span>
          </li>
        )
      })}
    </ol>
  )
}

function readDone(key: string): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, boolean>
  } catch {
    return {}
  }
}

function FinishOnEtsy({
  t,
  listingId,
  editUrl,
  hsCode,
  aiPhotos,
}: {
  t: EtsyPublishStrings
  listingId: string
  editUrl: string | null
  hsCode: string | null
  aiPhotos: number
}) {
  const key = `pexify.etsy-finish.${listingId}`
  const [done, setDone] = useState<Record<string, boolean>>(() => readDone(key))
  const [copied, setCopied] = useState(false)
  const tick = (id: string, value: boolean) => {
    const next = { ...done, [id]: value }
    setDone(next)
    try {
      localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // a private window keeps the ticks for this visit only
    }
  }
  const items: { id: string; title: React.ReactNode; hint?: string }[] = [
    { id: 'production_partner', title: t.checklist.production_partner, hint: t.checklist.production_partner_hint },
    ...(hsCode
      ? [
          {
            id: 'hs_code',
            title: (
              <span className="inline-flex flex-wrap items-center gap-2">
                {t.checklist.hs_code(hsCode)}
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    void navigator.clipboard?.writeText(hsCode).then(() => setCopied(true)).catch(() => setCopied(false))
                  }}
                >
                  {copied ? t.checklist.copied : t.checklist.copy}
                </Button>
              </span>
            ),
          },
        ]
      : []),
    ...(aiPhotos ? [{ id: 'ai', title: t.checklist.ai, hint: t.checklist.ai_hint(aiPhotos) }] : []),
    { id: 'gpsr', title: t.checklist.gpsr },
    { id: 'publish', title: t.checklist.publish, hint: t.checklist.publish_hint },
  ]
  return (
    <Card className="grid gap-3 p-5" aria-label={t.checklist.title}>
      <h2 className="m-0 text-base font-semibold text-[var(--color-text)]">{t.checklist.title}</h2>
      <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.checklist.hint}</p>
      <ul className="m-0 grid list-none gap-2.5 p-0">
        {items.map((item) => (
          <li key={item.id} className="flex items-start gap-2.5 text-[13.5px] text-[var(--color-text)]">
            <input
              id={`etsy-finish-${item.id}`}
              type="checkbox"
              className="mt-1"
              checked={Boolean(done[item.id])}
              onChange={(e) => tick(item.id, e.target.checked)}
            />
            <label htmlFor={`etsy-finish-${item.id}`}>
              <b>{item.title}</b>
              {item.hint ? <span className="block text-xs text-[var(--color-text-muted)]">{item.hint}</span> : null}
            </label>
          </li>
        ))}
      </ul>
      {editUrl ? (
        <a href={editUrl} target="_blank" rel="noreferrer" className="inline-flex justify-center rounded-md bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-fg)]">
          {t.openOnEtsy}
        </a>
      ) : null}
    </Card>
  )
}

export function EtsyPublishPanel({
  listingId,
  view,
  hsCode,
  aiPhotos,
  onBackToDraft,
}: {
  listingId: string
  view: PublishView
  hsCode: string | null
  aiPhotos: number
  onBackToDraft?: () => void
}) {
  const t = useT(etsyPublishI18n)
  const qc = useQueryClient()
  const [link, setLink] = useState('')
  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => post(path, body),
    onSuccess: (next) => qc.setQueryData(publishKey(listingId), next),
    onError: (err: Error) => toast.error(t.actionFailed, { description: err.message, audience: 'seller' }),
  })
  const status = view.status
  if (status === 'none') return null

  if (isRunning(status)) {
    return (
      <Card className="grid gap-3 p-5" aria-live="polite">
        <Banner tone="run" icon="…" title={t.running.title} body={t.running.body} />
        <StepList t={t} view={view} running />
      </Card>
    )
  }

  if (status === 'uncertain') {
    return (
      <Card className="grid gap-4 p-5">
        <Banner tone="warn" icon="?" title={t.uncertain.title} body={t.uncertain.body} />
        <div>
          <a href={ETSY_DRAFTS_URL} target="_blank" rel="noreferrer" className="inline-flex rounded-md border border-[var(--color-border-strong)] px-3 py-2 text-sm font-semibold text-[var(--color-text)]">
            {t.uncertain.openDrafts}
          </a>
        </div>
        <div className="grid gap-2 rounded-[var(--card-radius)] bg-[var(--color-surface-muted)] p-4">
          <h3 className="m-0 text-sm font-semibold text-[var(--color-text)]">{t.uncertain.linkTitle}</h3>
          <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.uncertain.linkBody}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="etsy-link-url"
              aria-label={t.uncertain.linkTitle}
              placeholder={t.uncertain.linkPlaceholder}
              className="max-w-md"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
            <Button
              disabled={!link.trim() || act.isPending}
              onClick={() =>
                act.mutate({ path: `/listings/${listingId}/etsy-publish/resolve`, body: { action: 'link', listing_url: link } })
              }
            >
              {t.uncertain.link}
            </Button>
          </div>
        </div>
        <div className="grid gap-2 rounded-[var(--card-radius)] bg-[var(--color-surface-muted)] p-4">
          <h3 className="m-0 text-sm font-semibold text-[var(--color-text)]">{t.uncertain.noneTitle}</h3>
          <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.uncertain.noneBody}</p>
          <div>
            <Button
              variant="outline"
              disabled={act.isPending}
              onClick={() => act.mutate({ path: `/listings/${listingId}/etsy-publish/resolve`, body: { action: 'no_listing' } })}
            >
              {t.uncertain.none}
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  if (status === 'failed') {
    return (
      <Card className="grid gap-3 p-5">
        <Banner tone="bad" icon="✕" title={t.rejected.title} body={t.rejected.body} />
        <StepList t={t} view={{ ...view, steps: { listing: view.steps.listing } }} running={false} />
        {onBackToDraft ? (
          <div>
            <Button onClick={onBackToDraft}>{t.rejected.back}</Button>
          </div>
        ) : null}
      </Card>
    )
  }

  const partial = status === 'partial'
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="grid min-w-0 gap-4">
        <Card className="grid gap-3 p-5">
          {partial ? (
            <Banner tone="warn" icon="!" title={t.partial.title} body={t.partial.body} />
          ) : (
            <Banner tone="ok" icon="✓" title={t.ok.title} body={t.ok.body} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            {view.edit_url ? (
              <a href={view.edit_url} target="_blank" rel="noreferrer" className="inline-flex rounded-md bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-fg)]">
                {t.openOnEtsy}
              </a>
            ) : null}
            {partial ? (
              <Button
                variant="outline"
                disabled={act.isPending}
                onClick={() => act.mutate({ path: `/listings/${listingId}/etsy-publish/retry` })}
              >
                {t.retry}
              </Button>
            ) : null}
            {view.etsy_listing_id ? (
              <span className="text-xs text-[var(--color-text-muted)]">{t.listingNo(view.etsy_listing_id)}</span>
            ) : null}
          </div>
          <StepList t={t} view={view} running={false} />
        </Card>
        <div className="flex items-center gap-2.5 rounded-[var(--card-radius)] border border-dashed border-[var(--color-border-strong)] px-4 py-3 text-[13px] text-[var(--color-text-muted)]">
          <span aria-hidden="true">🔒</span>
          <span>{t.locked}</span>
        </div>
      </div>
      <FinishOnEtsy t={t} listingId={listingId} editUrl={view.edit_url} hsCode={hsCode} aiPhotos={aiPhotos} />
    </div>
  )
}
